import { Suspense, useContext, useEffect, useRef, useState } from 'react';
import { Divider } from 'primereact/divider';
import { useDebounce } from 'primereact/hooks';
import { useWindowSize } from 'react-use';
import MlxButton from '../../../components/common/MlxButton/MlxButton';
import MlxSearch from '../../../components/common/MlxSearch/MlxSearch';
import { MOBILE_BREAKPOINT, PermissionKeys, SubPermissionKeys } from '../../../constants/constants';
import { CommonCtx } from '../../../context/CommonCtxProvider';
import withSidebar from '../../../hoc/withSideBar/withSideBar';
import AdminStaffList from './components/StaffList';
import './Staff.scss';
import logger from '../../../utils/logger';
import { lazy } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { _AddAdminStaffFormType, getAdminStaffList } from '../../../helpers/adiminStaffHelper';
import { ADMIN_STAFF_LIST } from '../../../constants/queryKeys';
import { checkActionSubPermission } from '../../../utils/common';
import { AuthCtx } from '../../../context/AuthCtxProvider';
import { _AdminStaff, _DatatablePageEvent, _Pagination, _ToastMessageProps } from '../../../constants/staticTypes';
import { defaultPaginationEvent, defaultToastData } from '../../../helpers/commonHelper';
import MlxConfirmPopup from '../../../components/common/MlxConfirmPopup/MlxConfirmPopup';
import MlxToast from '../../../components/common/MlxToast/MlxToast';

const AddStaffSidebar = lazy(() => import('./components/AddStaffSideBar'));

const searchColumns = ['firstName', 'lastName', 'email'];

function Staff() {
    const { width: screenWidth } = useWindowSize();
    const { showSideBar, setShowSideBar } = useContext(CommonCtx);
    const [searchValue, debouncedSearchValue, setSearchValue] = useDebounce('', 1200);
    const queryClient = useQueryClient();
    const { userType } = useContext(AuthCtx);

    const [searchBarVisible, setSearchBarVisible] = useState(screenWidth > MOBILE_BREAKPOINT);
    const [adminStaffAdded, setAdminStaffAdded] = useState<boolean>(false);
    const [isAdminStaffEdit, setIsAdminStaffEdit] = useState<boolean>(false);
    const [adminStaffEditData, setAdminStaffEditData] = useState<_AddAdminStaffFormType>();
    const [dataTableEvent, setDataTableEvent] = useState<_DatatablePageEvent>(defaultPaginationEvent);
    const [adminStaffListData, setAdminStaffListData] = useState<any>(null);
    const [toastMessage, setToastMessage] = useState<_ToastMessageProps>(defaultToastData);

    // const fetchAdminStaffList = async () => {
    //     try {
    //         const dataTableEvent = { page: 0, rows: 10, search: '' }; // Customize as needed
    //         const data = await queryClient.fetchQuery(ADMIN_STAFF_LIST, () =>
    //             getAdminStaffList(queryClient, dataTableEvent.page + 1, dataTableEvent.rows, dataTableEvent.search)
    //         );
    //     } catch (error) {
    //         console.error('Error fetching admin staff list:', error);
    //     }
    // };

    const adminStaffListQuery = useQuery({
        queryKey: ADMIN_STAFF_LIST,
        queryFn: () => getAdminStaffList(queryClient, (dataTableEvent.page ?? 0) + 1, dataTableEvent.rows, dataTableEvent.search),
        onSuccess: (response) => {
            const paginationData = response?.data?.data?.pagination as _Pagination;
            const responseData = response?.data?.data?.lists as _AdminStaff;
            setAdminStaffListData(responseData);
            setDataTableEvent((event) => ({ ...event, totalRows: paginationData.total }));
        },
        onError: (error) => logger.log('Failed to fetch admin staff list', error),
        refetchOnWindowFocus: false,
    });

    const adminStaffListQueryRef = useRef(adminStaffListQuery);

    useEffect(() => {
        adminStaffListQueryRef.current.refetch();
    }, [dataTableEvent?.forceUpdate]);

    useEffect(() => {
        const searchPayload = searchColumns.map((column) => `&search[${column}]=${debouncedSearchValue}`).join('');
        setDataTableEvent((event) => ({
            ...event,
            search: debouncedSearchValue.trim().length > 0 ? searchPayload : '',
            forceUpdate: event.forceUpdate + 1,
        }));
    }, [debouncedSearchValue]);

    useEffect(() => {
        if (adminStaffAdded) {
            adminStaffListQuery.refetch();
            setAdminStaffAdded(false);
        }
    }, [adminStaffAdded, adminStaffListQuery]);

    const openEditAdminStaffSidebar = (editableData: _AddAdminStaffFormType) => {
        setAdminStaffEditData(editableData);
        setIsAdminStaffEdit(true);
        setShowSideBar(true);
    };

    return (
        <>
            <MlxToast
                message={toastMessage.message}
                show={toastMessage.show}
                variant={toastMessage.variant}
                onClose={() => setToastMessage(defaultToastData)}
            />
            <MlxConfirmPopup />
            <Suspense fallback={<div>Loading...</div>}>
                <AddStaffSidebar
                    showSideBar={showSideBar}
                    setShowSideBar={setShowSideBar}
                    setAdminStaffAdded={setAdminStaffAdded}
                    isAdminStaffEdit={isAdminStaffEdit}
                    setIsAdminStaffEdit={setIsAdminStaffEdit}
                    adminStaffEditData={adminStaffEditData}
                />
            </Suspense>

            <div className="page-section">
                <span className="page-header">Manage Staff</span>
                <div className="page-content h-100">
                    <div className="content-header flex-grow-0">
                        <span className="header-title">{searchBarVisible ? 'Staff List' : ''}</span>
                        <div className="header-fields">
                            {userType === 'superAdmin' ||
                            checkActionSubPermission(queryClient, PermissionKeys.MANAGE_STAFF, SubPermissionKeys.ADD_STAFF) ? (
                                <MlxButton
                                    className="ms-2"
                                    removeLabelOnMobile={true}
                                    size="small"
                                    label="Add Staff"
                                    icon="ri-add-large-line"
                                    iconPos="right"
                                    onClick={() => setShowSideBar(true)}
                                />
                            ) : (
                                <></>
                            )}
                            <MlxSearch
                                className="ms-2 p-inputtext-sm"
                                removeLabelOnMobile={true}
                                placeholder="Search"
                                value={searchValue}
                                onChange={(event) => setSearchValue(event.target.value)}
                                searchBarVisible={setSearchBarVisible}
                                isLoading={adminStaffListQuery?.isFetching && !!debouncedSearchValue.trim().length}
                            />
                        </div>
                    </div>
                    <Divider className="content-divider" />
                    <div className="content-body flex-grow-1 overflow-hidden">
                        <AdminStaffList
                            dataTableEvent={dataTableEvent}
                            setDataTableEvent={setDataTableEvent}
                            openEditAdminStaffSidebar={openEditAdminStaffSidebar}
                            adminStaffListData={adminStaffListData}
                            setAdminStaffListData={setAdminStaffListData}
                            adminStaffListQuery={adminStaffListQuery}
                            setToastMessage={setToastMessage}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}

export default withSidebar(Staff);
